import { Link, useLocation } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { siteConfig } from "@/config/site";


interface BreadcrumbItem {
  name: string;
  path: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
  className?: string;
}

/**
 * SEO-friendly breadcrumb navigation with internal links.
 * Used for both user UX and crawler hierarchy understanding.
 */
export function Breadcrumbs({ items, className = "" }: BreadcrumbsProps) {
  const location = useLocation();
  
  // Auto-generate items if not provided
  const breadcrumbItems = items || generateItems(location.pathname);

  if (breadcrumbItems.length <= 1) return null;

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={`flex items-center space-x-2 text-xs font-medium text-muted-foreground mb-4 ${className}`}
    >
      <ol className="flex items-center space-x-1.5 list-none p-0 m-0">
        <li className="flex items-center">
          <Link
            to="/"
            className="hover:text-foreground transition-colors flex items-center gap-1"
            aria-label={siteConfig.name}
          >
            <Home className="h-3.5 w-3.5" />
            <span className="sr-only">{siteConfig.name}</span>

          </Link>
        </li>
        
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          return (
            <li key={item.path} className="flex items-center">
              <ChevronRight className="h-3.5 w-3.5 mx-1 opacity-50 shrink-0" />
              {isLast ? (
                <span className="font-medium text-foreground truncate max-w-[200px]" aria-current="page">
                  {item.name}
                </span>
              ) : (
                <Link
                  to={item.path}
                  className="hover:text-foreground transition-colors whitespace-nowrap"
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function generateItems(pathname: string): BreadcrumbItem[] {
  const paths = pathname.split("/").filter(Boolean);
  const items: BreadcrumbItem[] = [];
  
  let currentPath = "";
  paths.forEach((path) => {
    currentPath += `/${path}/`;
    // Simple prettification of slugs
    const name = path
      .replace(/-/g, " ")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
      
    items.push({ name, path: currentPath });
  });
  
  return items;
}
